import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { ProductPerformanceData, ProductPerformanceItem } from "../../types";

interface ProductPerformanceProps {
  data: ProductPerformanceData;
  currency: string;
}

type SubTab = "best" | "slow" | "margin" | "low" | "all";

export const ProductPerformance: React.FC<ProductPerformanceProps> = ({ data, currency }) => {
  const [subTab, setSubTab] = useState<SubTab>("best");

  const formatCurrency = (val: number) =>
    `${currency} ${Number(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  let items: ProductPerformanceItem[] = [];
  let title = "Best-Selling Products";

  switch (subTab) {
    case "best":
      items = data.bestSelling;
      title = "Top Best-Selling Products (by Volume)";
      break;
    case "slow":
      items = data.slowMoving;
      title = "Slow-Moving Products";
      break;
    case "margin":
      items = data.highMargin;
      title = "High-Margin Products (% Profit)";
      break;
    case "low":
      items = data.lowPerforming;
      title = "Lowest Performing Products (by Revenue)";
      break;
    case "all":
      items = data.allProducts;
      title = "All Products Sold";
      break;
  }

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {/* Sub Category Buttons */}
      <Box display="flex" flexWrap="wrap" gap={2}>
        <Button
          variant={subTab === "best" ? "primary" : "secondary"}
          size="small"
          onClick={() => setSubTab("best")}
        >
          Best Sellers ({data.bestSelling.length})
        </Button>
        <Button
          variant={subTab === "slow" ? "primary" : "secondary"}
          size="small"
          onClick={() => setSubTab("slow")}
        >
          Slow Moving ({data.slowMoving.length})
        </Button>
        <Button
          variant={subTab === "margin" ? "primary" : "secondary"}
          size="small"
          onClick={() => setSubTab("margin")}
        >
          High Margin ({data.highMargin.length})
        </Button>
        <Button
          variant={subTab === "low" ? "primary" : "secondary"}
          size="small"
          onClick={() => setSubTab("low")}
        >
          Low Performing ({data.lowPerforming.length})
        </Button>
        <Button
          variant={subTab === "all" ? "primary" : "secondary"}
          size="small"
          onClick={() => setSubTab("all")}
        >
          All Items ({data.allProducts.length})
        </Button>
      </Box>

      {/* Table Card */}
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={3}
        backgroundColor="default1"
        padding={4}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Text size={4} fontWeight="bold">
          {title}
        </Text>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fafafa" }}>
                <th style={{ padding: "10px" }}>Product Name</th>
                <th style={{ padding: "10px" }}>Variant</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Qty Sold</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Revenue</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Estimated Cost</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Gross Margin</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Margin %</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: "20px", textAlign: "center", color: "#888" }}>
                    No products found matching this filter.
                  </td>
                </tr>
              ) : (
                items.map((it, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{it.productName}</td>
                    <td style={{ padding: "10px", color: "#666" }}>
                      {it.variantName || "Default"}
                    </td>
                    <td style={{ padding: "10px", textAlign: "right", fontWeight: "bold" }}>
                      {it.quantitySold.toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "right",
                        color: "#2e7d32",
                        fontWeight: "bold",
                      }}
                    >
                      {formatCurrency(it.revenue)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "right", color: "#888" }}>
                      {formatCurrency(it.cost)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "right", fontWeight: "bold" }}>
                      {formatCurrency(it.grossMargin)}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "right",
                        fontWeight: "bold",
                        color: it.marginPercentage >= 20 ? "#2e7d32" : "#e65100",
                      }}
                    >
                      {it.marginPercentage}%
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Box>
    </Box>
  );
};
